// Source Documents — upload page.
//
// Two-step flow:
//   1. User fills metadata + drops file → POST /source-documents → server
//      returns the SourceDocument doc + a presigned S3 PUT URL.
//   2. Client PUTs the file directly to S3 via the presigned URL.
//   3. On success, navigate to the detail page where the user can trigger
//      ingestion.
//
// Route: /admin/knowledge/sources/new
//
// File constraints (mirror the backend `shared/ingestion/extract.ts`):
//   - Max 100 MB (sanity ceiling — books are 1–5 MB)
//   - Allowed types: .docx, .txt
//   - .doc (legacy binary Word) is NOT supported — pre-convert to .docx
//     (Word: Save As → Word Document). Don't accept them at the file
//     picker; the error is confusing when it surfaces post-upload at
//     ingestion time.
//   - .pdf is NOT wired yet (no MVP book is a PDF). Add here when
//     `extract.ts` gets a PDF handler.

import { useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Spinner } from '@gc-coach/design-system';
import type { SourceDocumentLanguage } from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { createSourceDocument, uploadFileToPresignedUrl } from '../api/endpoints.js';
import styles from './SourceDocuments.module.css';

const LANGUAGES: ReadonlyArray<SourceDocumentLanguage> = ['en', 'fr'];

const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const ALLOWED_CONTENT_TYPES = new Set([
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const ALLOWED_EXTENSIONS = ['.txt', '.docx'];

type UploadState =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'uploading' }
  | { kind: 'success'; id: string }
  | { kind: 'error'; message: string };

export function SourceDocumentUploadPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Metadata
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [prefaceBy, setPrefaceBy] = useState('');
  const [publicationYear, setPublicationYear] = useState('');
  const [isbn, setIsbn] = useState('');
  const [language, setLanguage] = useState<SourceDocumentLanguage>('en');

  // File
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Upload state
  const [upload, setUpload] = useState<UploadState>({ kind: 'idle' });

  const inFlight = upload.kind === 'creating' || upload.kind === 'uploading';

  function onFilePicked(picked: File) {
    if (picked.size > MAX_FILE_SIZE_BYTES) {
      setUpload({
        kind: 'error',
        message: t('sources.uploadErrorTooLarge'),
      });
      return;
    }
    // Normalise content-type for files where the browser may give an empty
    // string (.doc on some platforms): try the extension fallback.
    let contentType = picked.type;
    if (!contentType || !ALLOWED_CONTENT_TYPES.has(contentType)) {
      const lower = picked.name.toLowerCase();
      if (lower.endsWith('.txt')) contentType = 'text/plain';
      else if (lower.endsWith('.docx'))
        contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      // .doc + .pdf deliberately omitted — see file header comment.
    }
    if (!ALLOWED_CONTENT_TYPES.has(contentType)) {
      setUpload({
        kind: 'error',
        message: t('sources.uploadErrorBadType'),
      });
      return;
    }
    // Synthesize a File with the resolved content-type so the PUT carries
    // the correct header (the original File may have type='').
    const sanitized =
      picked.type === contentType
        ? picked
        : new File([picked], picked.name, { type: contentType });
    setFile(sanitized);
    setUpload({ kind: 'idle' });
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (inFlight) return;
    const picked = e.dataTransfer.files?.[0];
    if (picked) onFilePicked(picked);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file) {
      setUpload({ kind: 'error', message: t('sources.uploadErrorNoFile') });
      return;
    }
    if (!title.trim() || !author.trim()) {
      setUpload({
        kind: 'error',
        message: t('sources.uploadErrorMissingMetadata'),
      });
      return;
    }

    setUpload({ kind: 'creating' });
    const createResult = await createSourceDocument({
      title: title.trim(),
      author: author.trim(),
      ...(prefaceBy.trim() ? { preface_by: prefaceBy.trim() } : {}),
      ...(publicationYear.trim()
        ? { publication_year: Number(publicationYear.trim()) }
        : {}),
      ...(isbn.trim() ? { isbn: isbn.trim() } : {}),
      language,
      original_filename: file.name,
      file_size_bytes: file.size,
      content_type: file.type,
    });

    if (!createResult.ok) {
      setUpload({
        kind: 'error',
        message: t('sources.uploadErrorCreateFailed'),
      });
      return;
    }

    setUpload({ kind: 'uploading' });
    const putResult = await uploadFileToPresignedUrl(
      createResult.data.upload_url,
      file,
    );

    if (!putResult.ok) {
      setUpload({
        kind: 'error',
        message: t('sources.uploadErrorPutFailed'),
      });
      return;
    }

    // Success — give a beat for the user to see the success state, then
    // navigate to the detail page.
    setUpload({ kind: 'success', id: createResult.data.document.id });
    setTimeout(() => {
      navigate(`/admin/knowledge/sources/${createResult.data.document.id}`);
    }, 800);
  }

  return (
    <AdminShell
      activeKey="sources"
      breadcrumb={[
        t('sources.breadcrumbKnowledge'),
        t('sources.breadcrumbSources'),
        t('sources.uploadBreadcrumb'),
      ]}
      state={{ kind: 'normal' }}
    >
      <PageHeader title={t('sources.uploadTitle')} sub={t('sources.uploadSubTitle')} />

      <form className={styles.uploadForm} onSubmit={onSubmit}>
        {upload.kind === 'error' && (
          <div style={{ marginBottom: 16 }}>
            <Alert severity="danger" onDismiss={() => setUpload({ kind: 'idle' })}>
              {upload.message}
            </Alert>
          </div>
        )}
        {upload.kind === 'success' && (
          <div style={{ marginBottom: 16 }}>
            <Alert severity="info">{t('sources.uploadSuccess')}</Alert>
          </div>
        )}

        {/* Metadata fields */}
        <div className={styles.metaGrid}>
          <div className={styles.metaField}>
            <label className={styles.fieldLabel} htmlFor="sd-title">
              {t('sources.fieldTitle')}
            </label>
            <input
              id="sd-title"
              type="text"
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('sources.fieldTitlePlaceholder')}
              disabled={inFlight}
              required
            />
          </div>

          <div className={styles.metaField}>
            <label className={styles.fieldLabel} htmlFor="sd-author">
              {t('sources.fieldAuthor')}
            </label>
            <input
              id="sd-author"
              type="text"
              className={styles.input}
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder={t('sources.fieldAuthorPlaceholder')}
              disabled={inFlight}
              required
            />
          </div>

          <div className={styles.metaField}>
            <label className={styles.fieldLabel} htmlFor="sd-preface">
              {t('sources.fieldPrefaceBy')}
            </label>
            <input
              id="sd-preface"
              type="text"
              className={styles.input}
              value={prefaceBy}
              onChange={(e) => setPrefaceBy(e.target.value)}
              placeholder={t('sources.fieldPrefaceByPlaceholder')}
              disabled={inFlight}
            />
          </div>

          <div className={styles.metaField}>
            <label className={styles.fieldLabel} htmlFor="sd-year">
              {t('sources.fieldYear')}
            </label>
            <input
              id="sd-year"
              type="number"
              className={styles.input}
              value={publicationYear}
              onChange={(e) => setPublicationYear(e.target.value)}
              placeholder={t('sources.fieldYearPlaceholder')}
              disabled={inFlight}
              min={1900}
              max={2100}
            />
          </div>

          <div className={styles.metaField}>
            <label className={styles.fieldLabel} htmlFor="sd-isbn">
              {t('sources.fieldIsbn')}
            </label>
            <input
              id="sd-isbn"
              type="text"
              className={styles.input}
              value={isbn}
              onChange={(e) => setIsbn(e.target.value)}
              placeholder={t('sources.fieldIsbnPlaceholder')}
              disabled={inFlight}
            />
          </div>

          <div className={styles.metaField}>
            <label className={styles.fieldLabel} htmlFor="sd-lang">
              {t('sources.fieldLanguage')}
            </label>
            <select
              id="sd-lang"
              className={styles.input}
              value={language}
              onChange={(e) =>
                setLanguage(e.target.value as SourceDocumentLanguage)
              }
              disabled={inFlight}
            >
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* File drop zone */}
        <div
          className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            if (!inFlight) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const picked = e.target.files?.[0];
              if (picked) onFilePicked(picked);
            }}
            style={{ display: 'none' }}
          />
          {file ? (
            <div className={styles.dropzoneFile}>
              <strong>{file.name}</strong>
              <span>
                {(file.size / (1024 * 1024)).toFixed(2)} MB · {file.type || 'unknown'}
              </span>
              <span className={styles.dropzoneHint}>
                {t('sources.dropzoneReplaceHint')}
              </span>
            </div>
          ) : (
            <div className={styles.dropzonePrompt}>
              <div className={styles.dropzoneIcon} aria-hidden="true">⤓</div>
              <strong>{t('sources.dropzoneTitle')}</strong>
              <span>{t('sources.dropzoneBody')}</span>
              <span className={styles.dropzoneHint}>
                {t('sources.dropzoneFormats')} · max {MAX_FILE_SIZE_MB} MB
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.uploadFooter}>
          <Button
            kind="ghost"
            onClick={() => navigate('/admin/knowledge/sources')}
            disabled={inFlight}
            type="button"
          >
            {t('sources.uploadCancel')}
          </Button>
          <Button kind="primary" type="submit" disabled={inFlight || !file}>
            {inFlight ? <Spinner /> : t('sources.uploadSubmit')}
          </Button>
        </div>
      </form>
    </AdminShell>
  );
}
